import React from 'react';
import { useTranslation } from 'react-i18next';
import { Calendar, Plus, Trash2 } from 'lucide-react';
import { Card, CardContent } from '../ui/card';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../ui/table';

const TYPE_CONFIG = {
  NATIONAL:  { className: 'bg-blue-500' },
  REGIONAL:  { className: 'bg-purple-500' },
  RELIGIOUS: { className: 'bg-green-500' },
  COMPANY:   { className: 'bg-orange-500' },
  OPTIONAL:  { className: 'bg-gray-500' },
};

const getTypeBadge = (type, t) => {
  const config = TYPE_CONFIG[type] || TYPE_CONFIG.NATIONAL;
  return <Badge className={config.className}>{t(`holidayCommon.types.${type}`, { defaultValue: type })}</Badge>;
};

const formatDate = (dateString, language) => {
  if (!dateString) return '-';
  return new Date(dateString).toLocaleDateString(language, { day: '2-digit', month: 'long', year: 'numeric' });
};

const CalendrierDetailHolidaysTab = ({ holidays, calendarStatus, setIsAddHolidayModalOpen, confirmRemoveHoliday }) => {
  const { t, i18n } = useTranslation();

  return (
    <div className="space-y-4">
    <div className="flex items-center justify-between">
      <div>
        <h3 className="text-lg font-medium">{t("holidayCalendarComponents.detail.holidaysTitle", { count: holidays.length })}</h3>
        <p className="text-sm text-muted-foreground">{t("holidayCalendarComponents.detail.holidaysDesc")}</p>
      </div>
      {calendarStatus === 'DRAFT' && (
        <Button onClick={() => setIsAddHolidayModalOpen(true)}>
          <Plus className="mr-2 h-4 w-4" />
          {t("holidayCalendarComponents.detail.addHolidays")}
        </Button>
      )}
    </div>

    {holidays.length === 0 ? (
      <Card>
        <CardContent className="p-6">
          <div className="text-center py-8">
            <Calendar className="mx-auto h-12 w-12 text-gray-400" />
            <h3 className="mt-2 text-sm font-semibold text-foreground">{t("holidayCalendarComponents.detail.emptyHolidayTitle")}</h3>
            <p className="mt-1 text-sm text-muted-foreground">{t("holidayCalendarComponents.detail.emptyHolidayDesc")}</p>
            {calendarStatus === 'DRAFT' && (
              <div className="mt-6">
                <Button onClick={() => setIsAddHolidayModalOpen(true)}>
                  <Plus className="mr-2 h-4 w-4" />
                  {t("holidayCalendarComponents.detail.addHolidays")}
                </Button>
              </div>
            )}
          </div>
        </CardContent>
      </Card>
    ) : (
      <Card>
        <CardContent className="p-0">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>{t("holidayCommon.fields.name")}</TableHead>
                <TableHead>{t("holidayCommon.fields.date")}</TableHead>
                <TableHead>{t("holidayCommon.fields.type")}</TableHead>
                <TableHead>{t("holidayCommon.fields.country")}</TableHead>
                <TableHead>{t("holidayCommon.fields.description")}</TableHead>
                {calendarStatus === 'DRAFT' && <TableHead className="text-right">{t("holidayCommon.fields.actions")}</TableHead>}
              </TableRow>
            </TableHeader>
            <TableBody>
              {[...holidays]
                .sort((a, b) => new Date(a.date) - new Date(b.date))
                .map((holiday) => (
                  <TableRow key={holiday.id}>
                    <TableCell className="font-medium">{holiday.name}</TableCell>
                    <TableCell>{formatDate(holiday.date, i18n.language)}</TableCell>
                    <TableCell>{getTypeBadge(holiday.type, t)}</TableCell>
                    <TableCell>{holiday.country || '-'}</TableCell>
                    <TableCell className="max-w-xs truncate">{holiday.description || '-'}</TableCell>
                    {calendarStatus === 'DRAFT' && (
                      <TableCell className="text-right">
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => confirmRemoveHoliday(holiday)}
                          className="text-red-400 hover:text-red-300"
                        >
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      </TableCell>
                    )}
                  </TableRow>
                ))}
            </TableBody>
          </Table>
        </CardContent>
      </Card>
    )}
    </div>
  );
};

export default CalendrierDetailHolidaysTab;
